/* ========================================================== <keis-runtimes>
   The Battery run times page (sections/run-times.liquid).

   The figures are worked out in Liquid (snippets/runtime-data) and arrive
   as JSON, minutes per battery per heat setting, so this only draws them:

   - By garment: a chip per group (Jackets, Gloves...), a tile per garment
     in it; the chosen one shows each of its power sources with a bar per setting, scaled to its longest run.
   - Compare: a battery and a setting, and every garment that runs on that
     battery, longest first; the ones that do not are named underneath.

   The chosen garment is kept in the address (#j601-ultraflex-heated-jacket)
   so a link from a product page or an email lands on it. Every word comes
   from data-t-* attributes, which Liquid fills in the visitor's language.
   ========================================================================= */
(function () {
  'use strict';
  if (customElements.get('keis-runtimes')) return;

  var esc = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };
  var handleOf = function (url) { return String(url || '').split('/products/')[1] || ''; };
  var HEAT = ['var(--heat-1)', 'var(--heat-2)', 'var(--heat-3)'];
  /* The voltage and capacity under a battery's name, unless the name gives
     them already: a glove set's figures are per glove, and "4000mAh Set"
     over "2000mAh" reads as a mistake. */
  var showSpec = function (b) { return !!b.spec && !/mah/i.test(b.t); };

  class RunTimes extends HTMLElement {
    connectedCallback() {
      var raw = this.querySelector('[data-rt-data]');
      try { this.items = JSON.parse(raw ? raw.textContent : '[]'); } catch (e) { this.items = []; }
      this.items = this.items.filter(function (p) { return p && p.lv && p.lv.length; });
      if (!this.items.length) return;
      var self = this;

      this.buildTiles();
      this.buildCompare();

      this.querySelectorAll('[data-rt-mode]').forEach(function (b) {
        b.addEventListener('click', function () { self.mode(b.getAttribute('data-rt-mode')); });
      });

      var fromHash = decodeURIComponent(window.location.hash.slice(1));
      var start = this.items.find(function (p) { return handleOf(p.u) === fromHash; }) || this.items[0];
      this.pick(start, false);
      this.classList.add('is-ready');
    }

    t(key, fb) { return this.getAttribute('data-t-' + key) || fb; }

    /* "Up to 2h 30m", in the visitor's words: the same rounding and the
       same phrases as the product page's calculator. */
    dur(mins) {
      var h = Math.floor(mins / 60), m = mins % 60, body;
      if (!h) body = this.t('min', '[n] min').replace('[n]', m);
      else if (!m) body = this.t(h === 1 ? 'hour' : 'hours', '[n] hours').replace('[n]', h);
      else body = this.t('hm', '[h]h [m]m').replace('[h]', h).replace('[m]', m);
      return body;
    }
    upTo(mins) { return this.t('up-to', 'Up to [d]').replace('[d]', this.dur(mins)); }

    mode(which) {
      this.querySelectorAll('[data-rt-mode]').forEach(function (b) {
        b.setAttribute('aria-selected', b.getAttribute('data-rt-mode') === which ? 'true' : 'false');
      });
      this.querySelectorAll('[data-rt-view]').forEach(function (v) {
        v.hidden = v.getAttribute('data-rt-view') !== which;
      });
      if (which === 'compare') this.drawRank();
    }

    /* ── By garment ─────────────────────────────────────────────────────── */
    buildTiles() {
      var self = this;
      var box = this.querySelector('[data-rt-tiles]');
      this.gtabs = Array.prototype.slice.call(this.querySelectorAll('[data-rt-gtab]'));
      this.gtabs.forEach(function (tab) {
        var g = Number(tab.getAttribute('data-rt-gtab'));
        if (!self.items.some(function (p) { return p.g === g; })) { tab.hidden = true; return; }
        tab.addEventListener('click', function () {
          self.group(g);
          var first = self.items.find(function (p) { return p.g === g; });
          if (first && (!self.current || self.current.g !== g)) self.pick(first, true);
        });
      });
      this.items.forEach(function (p) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'keis-rt__tile';
        b.setAttribute('aria-pressed', 'false');
        b.innerHTML = (p.img ? '<img src="' + esc(p.img) + '" alt="" width="56" height="56" loading="lazy" decoding="async">' : '<span class="keis-rt__tile-ph"></span>') +
          '<span class="keis-rt__tile-name">' + esc(p.t) + '</span>';
        b.addEventListener('click', function () { self.pick(p, true); });
        p.tile = b;
        box.appendChild(b);
      });
    }

    /* One group's garments at a time, so the run times sit right under
       the garment chosen rather than a screen further down. */
    group(g) {
      this.gtabs.forEach(function (t) {
        var on = Number(t.getAttribute('data-rt-gtab')) === g;
        t.setAttribute('aria-pressed', on ? 'true' : 'false');
        /* On a phone the chips are one scrolling row: bring the chosen one
           into it, sideways only, so the page itself never jumps. */
        var row = t.parentElement;
        if (on && row && row.scrollWidth > row.clientWidth) {
          var left = t.offsetLeft - row.offsetLeft - 16;
          if (left < row.scrollLeft || t.offsetLeft - row.offsetLeft + t.offsetWidth > row.scrollLeft + row.clientWidth) row.scrollLeft = Math.max(0, left);
        }
      });
      this.items.forEach(function (p) { if (p.tile) p.tile.hidden = p.g !== g; });
    }

    pick(p, byVisitor) {
      this.current = p;
      this.group(p.g);
      this.items.forEach(function (x) { if (x.tile) x.tile.setAttribute('aria-pressed', x === p ? 'true' : 'false'); });
      this.drawPanel(p);
      if (byVisitor) {
        var h = handleOf(p.u);
        if (h && window.history && window.history.replaceState) window.history.replaceState(null, '', '#' + h);
      }
    }

    /* One sheet per garment, the same shape whichever garment it is: a row
       per battery under Low / Medium / High, and the supply lead as a strip
       along the bottom, there or not, so nothing jumps when the choice
       changes. Bars are scaled to the garment's longest run. */
    drawPanel(p) {
      var self = this;
      var panel = this.querySelector('[data-rt-panel]');
      var longest = 0;
      p.src.forEach(function (s) { s.m.forEach(function (m) { if (m > longest) longest = m; }); });
      var dot = function (i) { return '<i style="background:' + HEAT[Math.min(i, 2)] + '"></i>'; };

      var head = '<div class="keis-rt__grid-head" role="row">' +
        '<span role="columnheader">' + esc(this.t('power', 'Battery')) + '</span>' +
        p.lv.map(function (name, i) { return '<span role="columnheader" class="keis-rt__level">' + dot(i) + esc(name) + '</span>'; }).join('') +
      '</div>';

      var rows = p.src.map(function (s) {
        var note = s.inc ? self.t('included', 'Included') : self.t('sold', 'Sold separately');
        return '<div class="keis-rt__grid-row" role="row">' +
          '<div class="keis-rt__src" role="rowheader">' +
            '<a href="' + esc(s.u) + '" data-kss-no-badges>' + esc(s.t) + '</a>' +
            '<span class="keis-rt__tag' + (s.inc ? ' is-included' : '') + '">' + esc(note) + '</span>' +
          '</div>' +
          p.lv.map(function (name, i) {
            var m = s.m[i] || 0;
            var pct = longest && m ? Math.max(4, Math.round((m / longest) * 100)) : 0;
            return '<div class="keis-rt__cell" role="cell">' +
              '<span class="keis-rt__cell-level">' + dot(i) + esc(name) + '</span>' +
              '<span class="keis-rt__bar"><span style="width:' + pct + '%;background:' + HEAT[Math.min(i, 2)] + '"></span></span>' +
              '<span class="keis-rt__time">' + (m ? esc(self.upTo(m)) : '—') + '</span>' +
            '</div>';
          }).join('') +
        '</div>';
      }).join('');
      if (!p.src.length) rows = '<div class="keis-rt__grid-row keis-rt__grid-row--empty" role="row"><span role="cell">' + esc(this.t('none', '')) + '</span></div>';

      var plug = (this.querySelector('[data-rt-icon-plug]') || {}).innerHTML || '';
      var lead = '<div class="keis-rt__lead' + (p.lead ? '' : ' is-off') + '">' +
        '<span class="keis-rt__lead-icon" aria-hidden="true">' + plug + '</span>' +
        '<span class="keis-rt__lead-name">' + esc(this.t('lead', 'Vehicle supply lead')) + '</span>' +
        (p.lead ? '<span class="keis-rt__bar is-forever"><span></span></span>' : '') +
        '<span class="keis-rt__lead-text">' + esc(p.lead ? this.t('forever', 'Runs for as long as the bike does') : this.t('no-lead', 'Not for this garment')) + '</span>' +
      '</div>';

      var draws = p.w ? this.t('draws', '').replace('[w]', p.w).replace('[a]', p.a) : '';
      var wiring = this.getAttribute('data-wiring-url');
      panel.innerHTML =
        '<div class="keis-rt__sheet">' +
          '<div class="keis-rt__garment">' +
            (p.img ? '<img src="' + esc(p.img) + '" alt="" width="120" height="120" loading="lazy" decoding="async">' : '') +
            '<div class="keis-rt__garment-text">' +
              '<h2 class="keis-rt__garment-title">' + esc(p.t) + '</h2>' +
              (draws ? '<p class="keis-rt__draw">' + esc(draws) + (wiring && p.lead ? ' · <a href="' + esc(wiring) + '">' + esc(this.t('fuse', 'Which fuse?')) + '</a>' : '') + '</p>' : '') +
              '<a class="keis-rt__view-link" href="' + esc(p.u) + '" data-kss-no-badges>' + esc(this.t('view', 'View product')) + ' →</a>' +
            '</div>' +
          '</div>' +
          '<div class="keis-rt__grid" role="table">' + head + rows + '</div>' +
          lead +
        '</div>';
    }

    /* ── Compare ────────────────────────────────────────────────────────── */
    buildCompare() {
      var self = this;
      /* Every battery any garment takes, the most widely used first. */
      var byUrl = {};
      this.items.forEach(function (p) {
        p.src.forEach(function (s) {
          var b = byUrl[s.u] || (byUrl[s.u] = { t: s.t, spec: s.spec, u: s.u, n: 0 });
          b.n += 1;
        });
      });
      this.batteries = Object.keys(byUrl).map(function (k) { return byUrl[k]; })
        .sort(function (a, b) { return b.n - a.n; });
      this.levels = this.items[0].lv;
      this.battery = this.batteries[0] ? this.batteries[0].u : '';
      this.level = this.levels.length - 1;

      var bBox = this.querySelector('[data-rt-batteries]');
      var lBox = this.querySelector('[data-rt-levels]');
      if (!this.batteries.length) {
        var tab = this.querySelector('[data-rt-mode="compare"]');
        if (tab) tab.hidden = true;
        return;
      }
      this.batteries.forEach(function (b) {
        var c = document.createElement('button');
        c.type = 'button';
        c.className = 'keis-chip keis-chip--stacked';
        c.innerHTML = '<span class="keis-chip__label">' + esc(b.t) + '</span>' + (showSpec(b) ? '<span class="keis-chip__note">' + esc(b.spec) + '</span>' : '');
        c.setAttribute('aria-pressed', b.u === self.battery ? 'true' : 'false');
        c.addEventListener('click', function () {
          self.battery = b.u;
          bBox.querySelectorAll('button').forEach(function (x) { x.setAttribute('aria-pressed', x === c ? 'true' : 'false'); });
          self.drawRank();
        });
        bBox.appendChild(c);
      });
      this.levels.forEach(function (name, i) {
        var c = document.createElement('button');
        c.type = 'button';
        c.className = 'keis-chip keis-rt__level-chip';
        c.style.setProperty('--rt-heat', HEAT[Math.min(i, 2)]);
        c.innerHTML = '<i></i>' + esc(name);
        c.setAttribute('aria-pressed', i === self.level ? 'true' : 'false');
        c.addEventListener('click', function () {
          self.level = i;
          lBox.querySelectorAll('button').forEach(function (x) { x.setAttribute('aria-pressed', x === c ? 'true' : 'false'); });
          self.drawRank();
        });
        lBox.appendChild(c);
      });
    }

    drawRank() {
      var self = this, i = this.level;
      var on = [], off = [];
      this.items.forEach(function (p) {
        var s = p.src.find(function (x) { return x.u === self.battery; });
        if (s && s.m[i]) on.push({ p: p, m: s.m[i] });
        else off.push(p);
      });
      on.sort(function (a, b) { return b.m - a.m; });
      var longest = on.length ? on[0].m : 0;
      var colour = HEAT[Math.min(i, 2)];
      this.querySelector('[data-rt-rank]').innerHTML = on.map(function (r) {
        var pct = Math.max(4, Math.round((r.m / longest) * 100));
        return '<li class="keis-rt__rank-row">' +
          (r.p.img ? '<img src="' + esc(r.p.img) + '" alt="" width="40" height="40" loading="lazy" decoding="async">' : '<span></span>') +
          '<div class="keis-rt__rank-main">' +
            '<a href="' + esc(r.p.u) + '" data-kss-no-badges>' + esc(r.p.t) + '</a>' +
            '<span class="keis-rt__bar"><span style="width:' + pct + '%;background:' + colour + '"></span></span>' +
          '</div>' +
          '<span class="keis-rt__time">' + esc(self.upTo(r.m)) + '</span>' +
        '</li>';
      }).join('');
      var missing = this.querySelector('[data-rt-missing]');
      missing.hidden = !off.length;
      missing.textContent = off.length ? this.t('not-on', 'Not on this battery:') + ' ' + off.map(function (p) { return p.t; }).join(', ') : '';
    }
  }

  customElements.define('keis-runtimes', RunTimes);
})();
